import React, { useState } from "react";
import { apiFetch } from "../../services/api";
import DecisionPenLogo from "../../components/DecisionPenLogo";
import LoadingButton from "../../components/common/LoadingButton";
import GoogleContinueButton from "../components/GoogleContinueButton";
import AuthMagicLinkForm from "../components/auth/AuthMagicLinkForm";
import AuthModeToggle from "../components/auth/AuthModeToggle";
import AuthTermsCheckbox from "../components/auth/AuthTermsCheckbox";
import { AUTH_INPUT_CLASS, AUTH_LINK_CLASS } from "../components/auth/authFieldStyles";
import { continueWithGoogle, persistAuthSession } from "../auth/googleSso";
import { signupErrorMessageFromResponse } from "../utils/signupErrorMessage";
import {
  DEFAULT_ORGANIZATION_SIZE,
  displayNameFromEmail,
} from "../utils/signupDefaults";

const MARKETING_URL = "https://decisionpen.com";

const EXPECT_STEPS = [
  "Create your workspace — then onboarding can walk you through a sample landscape.",
  "AI proposes applications, technologies and relationships — you curate what joins the map.",
  "Ask the living landscape in plain English, with evidence.",
];

/**
 * Marketing signup entry for /demo (“See it live”).
 * Creates a normal org — sample/guided demo is offered in onboarding for new users.
 */
export default function V2SeeItLivePage({
  onAuthenticated,
  onNeedsGoogleSignup,
  onEmailSent,
  onSignIn,
  onBackToWebsite,
}) {
  const [mode, setMode] = useState("magic"); // magic | password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [terms, setTerms] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [error, setError] = useState("");

  const anyBusy = busy || googleBusy;

  const handleGoogle = async () => {
    setGoogleBusy(true);
    setError("");
    try {
      const result = await continueWithGoogle();
      if (result.ok) {
        persistAuthSession(result.data);
        onAuthenticated?.(result.data?.user);
        return;
      }
      if (result.needsSignupDetails) {
        onNeedsGoogleSignup?.({
          idToken: result.idToken,
          email: result.email,
          displayName: result.displayName,
        });
        return;
      }
      setError(result.message || "Google sign-in failed");
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/auth/magic-link/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          terms_accepted: terms,
          gdpr_consent: terms,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(
          (typeof data.detail === "string" && data.detail) ||
            "Could not send the sign-in link. Please try again."
        );
        return;
      }
      setSent(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");

    const trimmedEmail = email.trim();
    const org = organizationName.trim();
    if (!trimmedEmail || !org) {
      setError("Email and organisation name are required.");
      setBusy(false);
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      setBusy(false);
      return;
    }
    if (password.length > 72) {
      setError("Password must be 72 characters or less.");
      setBusy(false);
      return;
    }
    if (!terms) {
      setError("Please accept the Terms and Privacy Policy to create a workspace.");
      setBusy(false);
      return;
    }

    try {
      const now = new Date().toISOString();
      const response = await apiFetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: displayNameFromEmail(trimmedEmail),
          email: trimmedEmail,
          organization_name: org,
          organization_size: DEFAULT_ORGANIZATION_SIZE,
          password,
          terms_accepted: true,
          gdpr_consent: true,
          terms_accepted_at: now,
          gdpr_consent_at: now,
        }),
        timeout: 60000,
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.user?.email_verified) {
          onEmailSent?.(
            data.user?.email || trimmedEmail,
            data.verification_email_sent === true
          );
        } else {
          localStorage.setItem("access_token", data.access_token);
          localStorage.setItem("user", JSON.stringify(data.user));
          onAuthenticated?.(data.user);
        }
      } else {
        setError(await signupErrorMessageFromResponse(response));
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh bg-slate-50">
      <div className="mx-auto flex min-h-dvh max-w-6xl flex-col px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
        <header className="mb-6 flex items-center justify-between gap-3 sm:mb-10">
          <a
            href={MARKETING_URL}
            className="flex items-center gap-2.5 text-slate-900"
          >
            <DecisionPenLogo size={36} className="flex-shrink-0" />
            <span className="text-base font-semibold tracking-tight sm:text-lg">
              DecisionPen
            </span>
          </a>
          <button
            type="button"
            onClick={() =>
              onBackToWebsite
                ? onBackToWebsite()
                : window.location.assign(MARKETING_URL)
            }
            className="text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            Back to website
          </button>
        </header>

        <div className="grid flex-1 gap-8 lg:grid-cols-2 lg:gap-12 lg:items-start">
          <section className="lg:pt-2">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
              See it live
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Model a landscape from notes — then ask it anything
            </h1>
            <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg">
              Create a free workspace. If you&apos;re new, onboarding can offer a
              sample Customer Payments Hub path — from notes to a curated map
              you can ask in plain English.
            </p>
            <ol className="mt-6 space-y-3">
              {EXPECT_STEPS.map((step, i) => (
                <li
                  key={step}
                  className="flex gap-3 text-sm leading-relaxed text-slate-700 sm:text-[0.95rem]"
                >
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <p className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-600">
              Prefer to start from what you already trust? DecisionPen can also
              seed from a CMDB or inventory spreadsheet and enrich factsheets
              with How-and-Why from your documents.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
            <h2 className="text-lg font-semibold text-slate-900">
              Create your free account. No credit card needed.
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Email link, Google, or email and password — same workspace either
              way.
            </p>

            {sent ? (
              <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                Check your email — open the sign-in link{" "}
                <strong>on this device</strong> to land in your workspace.
              </div>
            ) : (
              <>
                <div className="mt-5">
                  <AuthModeToggle
                    value={mode}
                    onChange={(next) => {
                      setMode(next);
                      setError("");
                    }}
                    left={{ id: "magic", label: "Email link" }}
                    right={{ id: "password", label: "Email & password" }}
                  />
                </div>

                {mode === "magic" ? (
                  <div className="mt-4">
                    <AuthMagicLinkForm
                      email={email}
                      onEmailChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError("");
                      }}
                      terms={terms}
                      onTermsChange={setTerms}
                      onSubmit={handleMagicLink}
                      loading={busy}
                      disabled={anyBusy}
                      termsVariant="signup"
                      submitLabel="Email me a link to get started"
                      error={error}
                    />
                  </div>
                ) : (
                  <form
                    onSubmit={handlePasswordSignup}
                    className="mt-4 space-y-3"
                  >
                    <label className="block text-sm font-medium text-slate-800">
                      Email
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className={AUTH_INPUT_CLASS}
                        autoComplete="email"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-800">
                      Password
                      <input
                        type="password"
                        required
                        minLength={8}
                        maxLength={72}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="8–72 characters"
                        className={AUTH_INPUT_CLASS}
                        autoComplete="new-password"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-800">
                      Organisation name
                      <input
                        type="text"
                        required
                        value={organizationName}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        placeholder="Company or team"
                        className={AUTH_INPUT_CLASS}
                        autoComplete="organization"
                      />
                    </label>
                    <AuthTermsCheckbox
                      terms={terms}
                      setTerms={setTerms}
                      variant="signup"
                    />
                    <LoadingButton
                      type="submit"
                      loading={busy}
                      loadingLabel="Creating workspace…"
                      disabled={anyBusy}
                      invert
                      className="w-full rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      Create free workspace
                    </LoadingButton>
                  </form>
                )}

                <div className="mt-4">
                  <GoogleContinueButton
                    onClick={handleGoogle}
                    loading={googleBusy}
                    disabled={anyBusy}
                  />
                </div>
              </>
            )}

            {error && mode === "password" ? (
              <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </p>
            ) : null}

            {sent ? (
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setError("");
                }}
                className={`mt-5 w-full text-center ${AUTH_LINK_CLASS}`}
              >
                Use a different email
              </button>
            ) : (
              <p className="mt-5 text-center text-sm text-slate-600">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={onSignIn}
                  className={AUTH_LINK_CLASS}
                >
                  Sign in
                </button>
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
